import React, { useState, useEffect, useCallback, useRef } from "react";
import axios from "axios";
import { FaEye, FaEdit, FaTrash, FaPlus, FaAngleLeft, FaAngleRight, FaAngleDoubleLeft, FaAngleDoubleRight } from 'react-icons/fa';
import "bootstrap/dist/css/bootstrap.min.css";
import moment from "moment/moment";
import {
  Row,
  Col,
  Button,
  Alert,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Form,
  FormGroup,
  Label,
  Input,
  Table,
} from "reactstrap";
import { useSelector } from "react-redux";

import Filters from "./backlog/FilterKomponenBacklog";
import DataBacklog from "./datarecap/DataBacklog";
import { kecamatanOptions } from "./datarecap/constants";
import { useDebouncedTextFilters } from "../hooks/useDebouncedTextFilters";

const Backlog = () => {
  const [data, setData] = useState([]);
  const [error, setError] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [isDetailModalOpen, setIsDetailModalOpen] = useState(false);
  const [detailData, setDetailData] = useState(null);
  const [totalPages, setTotalPages] = useState(0);
  const [isMobile, setIsMobile] = useState(false);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [isLoading, setIsLoading] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [deleteItemId, setDeleteItemId] = useState(null);
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [questionnaires, setQuestionnaires] = useState([]);
  const { user } = useSelector((state) => state.authAdmin || {});
  
  // State untuk filter kecamatan di modal
  const [modalKecamatanFilter, setModalKecamatanFilter] = useState("");
  
  const [formData, setFormData] = useState({
    questionnaireId: '',
    namaKK: '',
    nomorKK: '',
    alamat: '',
    pekerjaan: '',
    penghasilan: '',
    jumlahAnggotaKK: '',
    kepemilikanTanah: '',
    usiaPernikahan: ''
  });
  const [editingId, setEditingId] = useState(null);

  const [filters, setFilters] = useState({
    kecamatan: "",
    desaKelurahan: "",
    searchName: "",
  });

  // State untuk menandai apakah sedang melakukan filter
  const [isFiltering, setIsFiltering] = useState(false);
  const fetchIdRef = useRef(0);
  const abortControllerRef = useRef(null);
  const queryFilters = useDebouncedTextFilters(filters, ["searchName"]);

  const handleFilterChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFilters((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? (checked ? "true" : "false") : value,
    }));
    setCurrentPage(1);
    setIsFiltering(true);
  };

  const fetchData = useCallback(async (page = 1, limit = itemsPerPage) => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    const controller = new AbortController();
    abortControllerRef.current = controller;
    const fetchId = ++fetchIdRef.current;

    setIsLoading(true);
    try {
      const trimmedSearchName = queryFilters.searchName?.trim();
      const params = {
        page,
        limit,
        ...(trimmedSearchName && { searchName: trimmedSearchName }),
        ...(queryFilters.kecamatan && queryFilters.kecamatan !== "" && queryFilters.kecamatan !== "Semua Kecamatan" && { kecamatan: queryFilters.kecamatan }),
        ...(queryFilters.desaKelurahan && queryFilters.desaKelurahan !== "" && queryFilters.desaKelurahan !== "Pilih Desa/Kelurahan" && { desaKelurahan: queryFilters.desaKelurahan }),
      };
      const response = await axios.get(`${process.env.REACT_APP_URL}/getbacklog`, {
        params,
        withCredentials: true,
        signal: controller.signal,
      });

      if (fetchId !== fetchIdRef.current) return;

      if (response.status === 200) {
        const responseData = response.data;
        setData(responseData.data || []);
        setTotalPages(responseData.pagination?.totalPages || 0);
        setCurrentPage(responseData.pagination?.currentPage || 1);
      }
    } catch (error) {
      if (error.code === "ERR_CANCELED" || error.name === "CanceledError") return;
      if (fetchId !== fetchIdRef.current) return;
      console.error("Error fetching data", error);
      setData([]);
    } finally {
      if (fetchId === fetchIdRef.current) {
        setIsLoading(false);
        setIsFiltering(false);
      }
    }
  }, [queryFilters, itemsPerPage]);

  // Fungsi fetchQuestionnaires dengan parameter kecamatan
  const fetchQuestionnaires = async (kecamatan = "") => {
    try {
      const params = {};
      if (kecamatan && kecamatan !== "" && kecamatan !== "Semua Kecamatan") {
        params.kecamatan = kecamatan;
      }
      
      const response = await axios.get(`${process.env.REACT_APP_URL}/questionnaires-for-backlog`, {
        params,
        withCredentials: true,
      });
      
      if (response.status === 200) {
        setQuestionnaires(response.data.data || []);
      }
    } catch (error) {
      console.error("Error fetching questionnaires", error);
      setError("Terjadi kesalahan saat memuat daftar questionnaire.");
    }
  };

  useEffect(() => {
    fetchData(currentPage, itemsPerPage);
  }, [currentPage, itemsPerPage, fetchData]);

  useEffect(() => {
    const checkIfMobile = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    checkIfMobile();
    window.addEventListener('resize', checkIfMobile);
    return () => window.removeEventListener('resize', checkIfMobile);
  }, []);

  const handlePageChange = (page) => {
    if (page < 1 || page > totalPages || page === currentPage) return;
    setCurrentPage(page);
  };

  const handleItemsPerPageChange = (event) => {
    const newLimit = parseInt(event.target.value);
    setItemsPerPage(newLimit);
    setCurrentPage(1);
  };
  
  const handleDeleteClick = (id) => {
    setDeleteItemId(id);
    setIsDeleteModalOpen(true);
  };

  const confirmDelete = async () => {
    try {
      await axios.delete(`${process.env.REACT_APP_URL}/deletebacklog/${deleteItemId}`, { withCredentials: true });
      fetchData(currentPage, itemsPerPage);
      setIsDeleteModalOpen(false);
    } catch (error) {
      console.error("Error deleting data:", error);
      setError("Gagal menghapus data. Silakan coba lagi.");
      setIsDeleteModalOpen(false);
    }
  };
  
  const openDetailModal = (item) => {
    setDetailData(item);
    setIsDetailModalOpen(true);
  };

  const handleFormChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  // Handler untuk filter kecamatan di modal
  const handleModalKecamatanChange = (e) => {
    const value = e.target.value;
    setModalKecamatanFilter(value);
    fetchQuestionnaires(value);
  };

  const resetForm = () => {
    setFormData({
      questionnaireId: '',
      namaKK: '',
      nomorKK: '',
      alamat: '',
      pekerjaan: '',
      penghasilan: '',
      jumlahAnggotaKK: '',
      kepemilikanTanah: '',
      usiaPernikahan: ''
    });
    setEditingId(null);
    setModalKecamatanFilter(""); // Reset filter
  };

  const handleAddClick = () => {
    resetForm();
    setIsAddModalOpen(true);
    fetchQuestionnaires(""); // Load all questionnaires
  };

  const handleEditClick = (item) => {
    setFormData({
      questionnaireId: item.QuestionnaireId || '',
      namaKK: item.namaKK || '',
      nomorKK: item.nomorKK || '',
      alamat: item.alamat || '',
      pekerjaan: item.pekerjaan || '',
      penghasilan: item.penghasilan || '',
      jumlahAnggotaKK: item.jumlahAnggotaKK || '',
      kepemilikanTanah: item.kepemilikanTanah || '',
      usiaPernikahan: item.usiaPernikahan || ''
    });
    setEditingId(item.id);
    setIsEditModalOpen(true);
    
    // Set filter kecamatan berdasarkan data yang sedang diedit
    if (item.Questionnaire?.kecamatan) {
      setModalKecamatanFilter(item.Questionnaire.kecamatan);
      fetchQuestionnaires(item.Questionnaire.kecamatan);
    } else {
      fetchQuestionnaires("");
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingId) {
        await axios.put(`${process.env.REACT_APP_URL}/updatebacklog/${editingId}`, formData, {
          withCredentials: true,
        });
        setIsEditModalOpen(false);
      } else {
        await axios.post(`${process.env.REACT_APP_URL}/backlog`, formData, {
          withCredentials: true,
        });
        setIsAddModalOpen(false);
      }
      resetForm();
      fetchData(currentPage, itemsPerPage);
    } catch (error) {
      console.error("Error saving backlog:", error);
      setError(error.response?.data?.message || "Terjadi kesalahan saat menyimpan data.");
    }
  };

  // Fungsi untuk mendapatkan pesan "tidak ada data" yang sesuai
  const getNoDataMessage = () => {
    if (isFiltering) {
      return "Tidak ada data yang tersedia sesuai filter tersebut";
    }
    return "Tidak ada data";
  };

  const contentStyle = isMobile 
    ? {} 
    : { marginLeft: "250px", marginRight: "20px" };

  return (
    <div className="table-responsive rounded border">
      <div className="main-content" style={contentStyle}>
        <Row className="mb-3 align-items-center">
          <Col>
            <h4>Data Backlog</h4>
          </Col>
          <Col className="text-end">
            <Button color="primary" onClick={handleAddClick}>
              <FaPlus /> Tambah Backlog
            </Button>
          </Col>
        </Row>

        <Row className="mb-3">
          <Col>
            <Filters
              filters={filters}
              kecamatanOptions={kecamatanOptions}
              onChange={handleFilterChange}
            />
          </Col>
        </Row>

        {error && (
          <Row>
            <Col>
              <Alert color="danger">{error}</Alert>
            </Col>
          </Row>
        )}

        <Row>
          <Col>
            <div className="table-responsive rounded border">
              <Table hover responsive className="mb-0">
                <thead className="table-light table-header">
                  <tr>
                    <th>No</th>
                    <th>Nomor KK</th>
                    <th>Nama KK</th>
                    <th>Alamat</th>
                    <th>Pekerjaan</th>
                    <th>Penghasilan</th>
                    <th>Kepemilikan Tanah</th>
                    <th>Usia Pernikahan</th>
                    <th>Data Pemilik Rumah</th>
                    <th>Kecamatan</th>
                    <th>Desa/Kelurahan</th>
                    <th>Aksi</th>
                  </tr>
                </thead>
                <tbody>
                  {isLoading ? (
                    <tr>
                      <td colSpan={12} className="text-center">Memuat...</td>
                    </tr>
                  ) : data && data.length > 0 ? (
                    data.map((item, idx) => (
                      <tr key={item.id || idx}>
                        <td>{(currentPage - 1) * itemsPerPage + idx + 1}</td>
                        <td>{item.nomorKK || '-'}</td>
                        <td>{item.namaKK || '-'}</td>
                        <td>{item.alamat || '-'}</td>
                        <td>{item.pekerjaan || '-'}</td>
                        <td>{item.penghasilan || '-'}</td>
                        <td>{item.kepemilikanTanah || '-'}</td>
                        <td>{item.usiaPernikahan || '-'} Tahun</td>
                        <td>{item.Questionnaire?.namaLengkapKK || '-'}</td>
                        <td>{item.Questionnaire?.kecamatan || '-'}</td>
                        <td>{item.Questionnaire?.desaKelurahan || '-'}</td>
                        <td className="text-center">
                          <div className="d-flex justify-content-center align-items-center gap-2">
                            <Button color="warning" className="p-0 me-2" size="sm" onClick={() => handleEditClick(item)}>
                              <FaEdit />
                            </Button>
                            {user && user.role === "admin" && (
                              <Button 
                               // color="danger"
                                size="sm" 
                                onClick={() => handleDeleteClick(item.id)}
                              >
                                <FaTrash />
                              </Button>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan={12} className="text-center py-4">
                        {getNoDataMessage()}
                      </td>
                    </tr>
                  )}
                </tbody>
              </Table>
            </div>
          </Col>
        </Row>

        {totalPages > 1 && (
          <Row className="align-items-center mt-3">
            <Col md="6" className="mb-2 mb-md-0">
              <div className="d-flex align-items-center gap-2">
                <span>Tampilan per halaman:</span>
                <Input type="select" value={itemsPerPage} onChange={handleItemsPerPageChange} style={{ width: 100 }}>
                  <option value={10}>10</option>
                  <option value={25}>25</option>
                  <option value={50}>50</option>
                </Input>
              </div>
            </Col>
            <Col md="6">
              <div className="d-flex justify-content-md-end gap-2">
                <Button size="sm" disabled={currentPage === 1} onClick={() => handlePageChange(1)}>
                  <FaAngleDoubleLeft />
                </Button>
                <Button size="sm" disabled={currentPage === 1} onClick={() => handlePageChange(currentPage - 1)}>
                  <FaAngleLeft />
                </Button>
                <div className="px-2 d-flex align-items-center">
                  Hal {currentPage} dari {totalPages}
                </div>
                <Button size="sm" disabled={currentPage === totalPages} onClick={() => handlePageChange(currentPage + 1)}>
                  <FaAngleRight />
                </Button>
                <Button size="sm" disabled={currentPage === totalPages} onClick={() => handlePageChange(totalPages)}>
                  <FaAngleDoubleRight />
                </Button>
              </div>
            </Col>
          </Row>
        )}

        {/* Delete Confirmation Modal */}
        <Modal isOpen={isDeleteModalOpen} toggle={() => setIsDeleteModalOpen(false)}>
          <ModalHeader toggle={() => setIsDeleteModalOpen(false)}>Konfirmasi Hapus</ModalHeader>
          <ModalBody>
            Apakah Anda yakin ingin menghapus data ini?
          </ModalBody>
          <ModalFooter>
            <Button color="danger" onClick={confirmDelete}>Hapus</Button>
            <Button color="secondary" onClick={() => setIsDeleteModalOpen(false)}>Batal</Button>
          </ModalFooter>
        </Modal>

        {/* DataBacklog Modal */}
        <DataBacklog
          isOpen={isDetailModalOpen}
          onClose={() => setIsDetailModalOpen(false)}
          backlogData={detailData}
        />

        {/* Add/Edit Backlog Modal */}
        <Modal isOpen={isAddModalOpen || isEditModalOpen} toggle={() => {
          setIsAddModalOpen(false);
          setIsEditModalOpen(false);
          resetForm();
        }} size="md">
          <ModalHeader toggle={() => {
            setIsAddModalOpen(false);
            setIsEditModalOpen(false);
            resetForm();
          }}>
            {editingId ? 'Edit Backlog' : 'Tambah Backlog'}
          </ModalHeader>
          <div className="home-logo-container">
            <img src="/images/logobaru.svg" alt="Logo Aplikasi" className="home-logo"/>
          </div>
          <Form onSubmit={handleSubmit}>
            <ModalBody>
              {/* Filter Kecamatan untuk dropdown Questionnaire */}
              <FormGroup>
                <Label for="modalKecamatanFilter">Filter Kecamatan</Label>
                <Input
                  type="select"
                  name="modalKecamatanFilter"
                  id="modalKecamatanFilter"
                  value={modalKecamatanFilter}
                  onChange={handleModalKecamatanChange}
                >
                  <option value="">Semua Kecamatan</option>
                  {Object.keys(kecamatanOptions).map((kec) => (
                    <option key={kec} value={kec}>
                      {kec}
                    </option>
                  ))}
                </Input>
                <small className="text-muted">Filter Data Backlog</small>
              </FormGroup>

              <FormGroup>
                <Label for="questionnaireId">Pilih Questionnaire *</Label>
                <Input
                  type="select"
                  name="questionnaireId"
                  id="questionnaireId"
                  value={formData.questionnaireId}
                  onChange={handleFormChange}
                  required
                >
                  <option value="">Pilih Questionnaire</option>
                  {questionnaires.map((q) => (
                    <option key={q.id} value={q.id}>
                      {q.namaLengkapKK} - {q.alamatRumah} ({q.jumlahKK} KK) - {q.kecamatan}
                    </option>
                  ))}
                </Input>
                {questionnaires.length === 0 && (
                  <small className="text-muted">Tidak ada questionnaire yang tersedia untuk kecamatan yang dipilih</small>
                )}
              </FormGroup>

              <FormGroup>
                <Label for="namaKK">Nama KK *</Label>
                <Input
                  type="text"
                  name="namaKK"
                  id="namaKK"
                  value={formData.namaKK}
                  onChange={handleFormChange}
                  required
                />
              </FormGroup>

              <FormGroup>
                <Label for="nomorKK">Nomor KK *</Label>
                <Input
                  type="text"
                  name="nomorKK"
                  id="nomorKK"
                  value={formData.nomorKK}
                  onChange={handleFormChange}
                  required
                />
              </FormGroup>

              <FormGroup>
                <Label for="alamat">Alamat *</Label>
                <Input
                  type="text"
                  name="alamat"
                  id="alamat"
                  value={formData.alamat}
                  onChange={handleFormChange}
                  required
                />
              </FormGroup>

              <FormGroup>
                <Label for="pekerjaan">Pekerjaan *</Label>
                <Input
                  type="text"
                  name="pekerjaan"
                  id="pekerjaan"
                  value={formData.pekerjaan}
                  onChange={handleFormChange}
                  required
                />
              </FormGroup>

              <FormGroup>
                <Label for="penghasilan">Penghasilan *</Label>
                <Input
                  type="number"
                  name="penghasilan"
                  id="penghasilan"
                  value={formData.penghasilan}
                  onChange={handleFormChange}
                  required
                />
              </FormGroup>

              <FormGroup>
                <Label for="jumlahAnggotaKK">Jumlah Anggota KK *</Label>
                <Input
                  type="number"
                  name="jumlahAnggotaKK"
                  id="jumlahAnggotaKK"
                  value={formData.jumlahAnggotaKK}
                  onChange={handleFormChange}
                  required
                />
              </FormGroup>

              <FormGroup>
                <Label for="kepemilikanTanah">Kepemilikan Tanah *</Label>
                <Input
                  type="text"
                  name="kepemilikanTanah"
                  id="kepemilikanTanah"
                  value={formData.kepemilikanTanah}
                  onChange={handleFormChange}
                  required
                />
              </FormGroup>

              <FormGroup>
                <Label for="usiaPernikahan">Usia Pernikahan (Tahun) *</Label>
                <Input
                  type="number"
                  name="usiaPernikahan"
                  id="usiaPernikahan"
                  value={formData.usiaPernikahan}
                  onChange={handleFormChange}
                  required
                />
              </FormGroup>
            </ModalBody>
            <ModalFooter>
              <Button color="primary" type="submit">
                {editingId ? 'Update' : 'Simpan'}
              </Button>
              <Button color="secondary" onClick={() => {
                setIsAddModalOpen(false);
                setIsEditModalOpen(false);
                resetForm();
              }}>
                Batal
              </Button>
            </ModalFooter>
          </Form>
        </Modal>
      </div>
    </div>
  );
};

export default Backlog;